import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { AppState } from 'store';
import useAuth from 'hooks/useAuth';
import { ui } from 'util/ui';
import useOrgSettings from 'hooks/useOrgSettings';

/**
 * UI refresh header: the customer's mark and name top left, Projects / Admin, and the account menu.
 * The logo and names come from Admin > Settings (defaults/organisation), so each customer sees their own.
 */
const AppHeader: FC = () => {
  const dispatch = useDispatch();
  const { signOut, isAdmin } = useAuth();
  const { user } = useSelector((state: AppState) => state.auth);
  const pathname: string = useSelector((state: any) => state.router?.location?.pathname || '/');
  const org = useOrgSettings();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  const go = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(push(path));
  };
  const onAdmin = pathname.startsWith('/admin');
  const initials = (user?.email || '?').slice(0, 2).toUpperCase();
  const navLink = (active: boolean): React.CSSProperties => ({
    textDecoration: 'none',
    color: active ? ui.ink : ui.muted,
    fontWeight: active ? 600 : 500,
    fontSize: 14,
    padding: '12px 14px',
    borderRadius: 10,
    background: active ? ui.subtle : 'transparent',
    whiteSpace: 'nowrap', // never break "Projects" into letters on a phone
    flex: 'none',
  });

  return (
    <header
      className="archy-header"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 64,
        boxSizing: 'border-box',
        background: ui.surface,
        borderBottom: `1px solid ${ui.line}`,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 24px',
        fontFamily: ui.font,
      }}
    >
      <a href="/" onClick={go('/')} style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: ui.ink, minWidth: 0 }}>
        {org.logoURL ? (
          <img src={org.logoURL} alt="" style={{ height: 36, maxWidth: 140, objectFit: 'contain' }} />
        ) : (
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: ui.accent,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 18,
              flex: 'none',
            }}
          >
            {(org.companyName || 'A').charAt(0).toUpperCase()}
          </span>
        )}
        {/* One line: company, then the database name in a quieter colour; no separator (Aaron, 5 Nov 2026). */}
        <span style={{ fontSize: 15, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          <span style={{ fontWeight: 700 }}>{org.companyName}</span>
          {org.databaseName && (
            <span className="archy-hide-phone" style={{ fontWeight: 500, color: ui.muted, marginLeft: 6 }}>
              {org.databaseName}
            </span>
          )}
        </span>
      </a>
      <span style={{ flex: 1 }} />
      <nav aria-label="Main" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <a href="/" onClick={go('/')} style={navLink(!onAdmin)} aria-current={!onAdmin ? 'page' : undefined}>
          Projects
        </a>
        {isAdmin && (
          <a href="/admin" onClick={go('/admin')} style={navLink(onAdmin)} aria-current={onAdmin ? 'page' : undefined}>
            Admin
          </a>
        )}
        <div ref={menuRef} style={{ position: 'relative', marginLeft: 8 }}>
          <button
            type="button"
            className="archy-btn"
            aria-label={`Account: ${user?.email || ''}`}
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => setMenu(!menu)}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              background: ui.ink,
              color: '#FFFFFF',
              font: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {initials}
          </button>
          {menu && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                right: 0,
                top: 48,
                background: ui.surface,
                border: `1px solid ${ui.line}`,
                borderRadius: 12,
                boxShadow: '0 12px 30px rgba(0,0,0,0.14)',
                padding: 6,
                minWidth: 220,
              }}
            >
              <div style={{ padding: '8px 10px', fontSize: 13, color: ui.muted, wordBreak: 'break-all' }}>{user?.email}</div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  signOut();
                }}
                style={{ width: '100%', textAlign: 'left', height: 40, padding: '0 10px', border: 'none', background: 'transparent', borderRadius: 8, font: 'inherit', fontSize: 14, color: ui.ink, cursor: 'pointer' }}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default AppHeader;
